import React from 'react'

import useNameFormatter from '@divvy-web/hooks.usenameformatter'
import { FormattedMessage } from '@divvy-web/i18n'
import { FormCheckboxInput, FormDateInput, FormDropdown } from '@divvy-web/skylab.form'
import { Control, useController } from '@divvy-web/skylab.react-hook-form'

import MaskedFormTextInput from '@sputnik/components/MaskedFormTextInput'
import { getFormattedSsn } from '@sputnik/components/utils'
import { AddPersonForm } from '@sputnik/schema/creditApplication/addPerson'
import { removeNonNumericCharacters } from '@sputnik/utils/dataUtils'

import { useGetCountryCodes } from './GetCountryCodes.gql'
import styles from './styles.module.scss'

interface SSNPassportInputProps {
  className?: string
  control: Control<AddPersonForm>
  disabled?: boolean
}

const SSNPassportInput = ({ className = '', control, disabled }: SSNPassportInputProps) => {
  const [getClassName] = useNameFormatter('SSNPassportInput')

  const { data } = useGetCountryCodes()

  const countryOptions =
    data?.countryCodes?.map((country) => ({
      label: country?.displayName,
      value: country?.isoCode2,
    })) || []

  const {
    field: { value: isNonUSCitizen, onChange: onIsNonUsCitizenChange },
  } = useController({
    control,
    name: 'isNonUSCitizen',
  })

  const {
    field: { onChange: onSsnChange },
  } = useController({
    control,
    name: 'ssn',
  })
  const {
    field: { onChange: onPassportNumberChange },
  } = useController({
    control,
    name: 'passportNumber',
  })
  const {
    field: { onChange: onPassportExpirationChange },
  } = useController({
    control,
    name: 'passportExpiration',
  })
  const {
    field: { onChange: onPassportCountryChange },
  } = useController({
    control,
    name: 'passportCountry',
  })

  return (
    <div className={`${styles[getClassName(className)]} dd-privacy-mask`}>
      <div className={styles[getClassName('ssn-and-checkbox')]}>
        <div>
          {!isNonUSCitizen && (
            <MaskedFormTextInput
              className={`${styles[getClassName('ssn-input')]} dd-privacy-mask`}
              control={control}
              disabled={disabled}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Social Security number'
                    id='sputnik.SSNPassportInput__+4ZP+O'
                  />
                </span>
              }
              maskedValue={(value) => getFormattedSsn(value, true)}
              maxLength={11}
              name='ssn'
              placeholder='000-00-0000'
              sanitize={removeNonNumericCharacters}
              unmaskedValue={getFormattedSsn}
            />
          )}
          <FormCheckboxInput
            checkboxLabel={
              <span className='dd-privacy-allow'>
                <FormattedMessage
                  defaultMessage='Non US citizen—no Social Security number'
                  id='sputnik.SSNPassportInput__P9oBUB'
                />
              </span>
            }
            control={control}
            disabled={disabled}
            name='isNonUSCitizen'
            onChange={(e) => {
              onIsNonUsCitizenChange(e)
              if (e.target.checked) {
                onSsnChange({ value: '', display: '' })
              } else {
                onPassportNumberChange({ value: '', display: '' })
                onPassportCountryChange('')
                onPassportExpirationChange(undefined)
              }
            }}
          />
        </div>
      </div>
      {isNonUSCitizen && (
        <div className={`${styles[getClassName('passport-inputs')]} dd-privacy-mask`}>
          <div>
            <MaskedFormTextInput
              className='dd-privacy-mask'
              control={control}
              disabled={disabled}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Passport number'
                    id='sputnik.SSNPassportInput__7DeEPg'
                  />
                </span>
              }
              maskedValue={(value: string) => value.replace(/.(?=.{4,}$)/g, '*')}
              name='passportNumber'
              unmaskedValue={(value: string) => value}
            />
          </div>
          <div>
            <FormDropdown
              isSearchable
              className='dd-privacy-mask'
              control={control}
              disabled={disabled}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Issuing country'
                    id='sputnik.SSNPassportInput__GdcAVX'
                  />
                </span>
              }
              name='passportCountry'
              options={countryOptions}
            />
          </div>
          <div>
            <FormDateInput
              shouldRenderCalendar
              addCharactersAtPositions={{
                2: '/',
                5: '/',
              }}
              className='dd-privacy-mask'
              clearInvalidInput={false}
              control={control}
              disabled={disabled}
              formatOptions={{
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }}
              label={
                <span className='dd-privacy-allow'>
                  <FormattedMessage
                    defaultMessage='Expiration date'
                    id='sputnik.SSNPassportInput__CICBj0'
                  />
                </span>
              }
              maxLength={10}
              name='passportExpiration'
              placeholderGuide='MM/DD/YYYY'
              onChange={(_, date) => {
                if (!date) {
                  return
                }
                onPassportExpirationChange(new Date(date))
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

export default SSNPassportInput
